// Text ingest pipeline (§6): pass1-prepare → pass2-normalize (fanout) →
// pass3-merge → pass4-mention-resolve → compile (deterministic assembler) →
// pass5-extract → SERIAL govern. The front (through assemble+extract) handles the
// context-local body in parallel; the serial govern pass then walks episodes in
// order carrying a running context to decide identity/importance/durable-states
// WITH plot understanding (see serial-govern.ts), replacing the old parallel
// curate/recall/refine/state-reconcile compensation. Workspace lifecycle per §5.3.

import * as fs from "node:fs";
import * as path from "node:path";

import { CliError, EXIT_INPUT, sha256Text } from "../../common.js";
import { assemble, type NameScript } from "../../domain/ingest/assembler.js";
import { chunkSource, type Chunk } from "../../domain/ingest/chunker.js";
import { parseJsonl } from "../../domain/ingest/jsonl-parser.js";
import { validateProductionStandard } from "../../domain/ingest/production-validation.js";
import { renumberIds } from "../../domain/ingest/renumber.js";
import type { IngestRecord } from "../../domain/ingest/records.js";
import { validateScriptV3 } from "../../domain/ingest/validation.js";
import { requireScriptV3 } from "../../domain/script/v3/parse.js";
import {
  newScriptV4FromV3WithIdentity,
  type V4IdentityMap,
} from "../../domain/script/v4/from-v3.js";
import { validateScriptV4 } from "../../domain/script/v4/validation.js";
import { resolveSource } from "../../infra/ingest/source-resolver.js";
import { readTextSource } from "../../infra/ingest/text-reader.js";
import { WorkspaceStore } from "../../infra/ingest/workspace-store.js";
import { runTextNormalize, parseNormalizeOutput } from "../../llm/tasks/ingest/text-normalize.js";
import type { LlmTaskExecutor } from "../../llm/types.js";
import { detectBodyStart } from "./body-start.js";
import { mapSettled } from "./fanout.js";
import { runSerialGovern } from "./serial-govern.js";
import { assetAuditWarnings, auditWorkspaceScriptAssets } from "./final-asset-audit.js";
import { Timings } from "./timings.js";
import { UsageLedger, type UsageScope } from "./usage-report.js";

const PIPELINE_VERSION = "12";

export interface TextIngestOptions {
  sourcePath: string;
  workspacePath: string;
  executor: LlmTaskExecutor;
  concurrency?: number;
  force?: boolean;
  signal?: AbortSignal;
}

export interface TextIngestReport {
  workspacePath: string;
  chunks: number;
  reused: number;
  records: number;
  notes: number;
  assets: { actors: number; locations: number; props: number };
  scriptPath: string;
  validation: { ok: boolean; errors: number; warnings: number };
}

const pad = (n: number): string => String(n).padStart(3, "0");
const serializeJsonl = (records: IngestRecord[]): string => records.map((r) => JSON.stringify(r)).join("\n") + "\n";

function checkLifecycle(store: WorkspaceStore, sourceSha: string, force: boolean): void {
  if (store.has("manifest.json")) {
    const manifest = store.readJson<{ pipeline_version?: string; source?: { sha256?: string } }>("manifest.json");
    if (manifest.source?.sha256 && manifest.source.sha256 !== sourceSha) {
      throw new CliError("This workspace was built from a different source. --force cannot switch sources.", {
              exitCode: EXIT_INPUT,
              errorCode: "INGEST_SOURCE_MISMATCH",
              hint: "Choose a fresh --workspace-path for the new source.",
              data: { actual: ["workspace source differs from --source-path"] },
            });
    }
    if (!force && manifest.pipeline_version && manifest.pipeline_version !== PIPELINE_VERSION) {
      throw new CliError("This workspace was created by an older text-ingest pipeline.", {
              exitCode: EXIT_INPUT,
              errorCode: "INGEST_PIPELINE_MISMATCH",
              hint: "Rerun `scriptctl ingest --force` with the same source to rebuild its checkpoints.",
              data: { actual: [`workspace pipeline ${manifest.pipeline_version}; current ${PIPELINE_VERSION}`] },
            });
    }
  }
  if (force) {
    for (const dir of ["pass1", "pass2", "pass3", "pass5", "serial"]) fs.rmSync(store.path(dir), { recursive: true, force: true });
    for (const f of [
      "body.json",
      "script.initial.json",
      "script.json",
      "validation.json",
      "asset-audit.json",
      "notes.json",
      "issues.json",
      "provisional_assets.json",
      "usage.json",
      "v4_identity_map.json",
    ]) fs.rmSync(store.path(f), { force: true });
  }
}

export async function runTextIngest(opts: TextIngestOptions): Promise<TextIngestReport> {
  const resolved = resolveSource(opts.sourcePath);
  if (resolved.kind !== "text") {
    throw new CliError("runTextIngest received a non-text source.", {
            exitCode: EXIT_INPUT,
            errorCode: "INGEST_WRONG_PIPELINE",
          });
  }
  const text = await readTextSource(resolved.path, resolved.ext);
  const sourceSha = sha256Text(text);

  const store = new WorkspaceStore(opts.workspacePath);
  store.ensure();
  checkLifecycle(store, sourceSha, opts.force ?? false);

  // Accurate per-run timing → workspace/timings.json (view --watch / ingest status).
  const timings = new Timings((t) => store.writeJson("timings.json", t));
  const usage = new UsageLedger(store, opts.executor);
  let runStatus: "succeeded" | "failed" = "failed";
  try {
  const prepareStartedMs = Date.now();

  // pass0: locate where the real screenplay body begins and drop the planning
  // front matter (大纲/角色卡/宏观结构/一卡集纲) before it. Cheap tool-mode LLM scan,
  // cached under pass1/ so a rerun skips it; --force wipes pass1 and recomputes.
  let bodyStart = 0;
  if (!opts.force && store.has("pass1/body_start.json")) {
    bodyStart = store.readJson<{ line?: number }>("pass1/body_start.json").line ?? 0;
    opts.executor.recordReuse("ingest.body-start");
  } else {
    const scan = await detectBodyStart(opts.executor, text, opts.signal);
    bodyStart = scan.line;
    store.writeJson("pass1/body_start.json", scan);
  }
  const lines0 = text.split("\n");
  const bodyText = bodyStart > 0 ? lines0.slice(bodyStart).join("\n") : text;
  // The stripped front matter (角色卡/设定) — capped — is fed to serial govern as an
  // 原文设定 reference so descriptions can draw on the source's own wording.
  const sourceRef = bodyStart > 0 ? lines0.slice(0, bodyStart).join("\n").trim().slice(0, 6000) : "";

  // pass1: prepare + chunk (the body only; full source kept for reference).
  const chunks = chunkSource(bodyText);
  store.writeText("pass1/source.txt", text);
  store.writeJson("pass1/chunks.json", chunks.map((c) => ({ index: c.index, core_line_start: c.core_line_start, core_line_end: c.core_line_end })));
  store.writeJson("manifest.json", {
    version: 1,
    pipeline_version: PIPELINE_VERSION,
    updated: new Date().toISOString(),
    source: { path: path.resolve(resolved.path), sha256: sourceSha, kind: "text", ext: resolved.ext },
    chunks: chunks.length,
  });
  usage.flush({
    scope: { kind: "phase", phase: "prepare" },
    status: "succeeded",
    startedAtMs: prepareStartedMs,
  });

  // pass2: normalize fanout with per-chunk structured JSON checkpoint.
  let reused = 0;
  timings.phaseStart("normalize");
  const settled = await mapSettled(chunks, opts.concurrency ?? 30, async (chunk: Chunk) => {
    const jsonRel = `pass2/chunk_${pad(chunk.index)}.json`;
    const errorRel = `pass2/chunk_${pad(chunk.index)}.error.json`;
    if (store.has(jsonRel) && !opts.force) {
      const records = parseNormalizeOutput(store.readJson<unknown>(jsonRel), chunk);
      store.clearError(errorRel);
      reused++;
      opts.executor.recordReuse("ingest.text-normalize");
      return records;
    }
    const { raw, records } = await runTextNormalize(opts.executor, chunk, opts.signal);
    store.writeText(jsonRel, raw);
    store.writeText(`pass2/chunk_${pad(chunk.index)}.jsonl`, serializeJsonl(records));
    store.clearError(errorRel);
    return records;
  });

  const failures = settled.filter((s): s is Extract<typeof s, { ok: false }> => !s.ok);
  for (const f of failures) {
    const message = f.error instanceof Error ? f.error.message : String(f.error);
    store.writeJson(`pass2/chunk_${pad(f.index)}.error.json`, { chunk: f.index, error: message });
  }
  usage.flush({
    scope: { kind: "phase", phase: "normalize" },
    status: failures.length ? "failed" : "succeeded",
  });
  if (failures.length > 0) {
    throw new CliError(`${failures.length}/${chunks.length} chunk(s) failed normalization.`, {
            exitCode: EXIT_INPUT,
            errorCode: "INGEST_NORMALIZE_INCOMPLETE",
            hint: "Inspect pass2/chunk_NNN.error.json and rerun `scriptctl ingest` to resume.",
            data: { actual: failures.map((f) => `chunk ${f.index}`) },
          });
  }
  timings.phaseEnd("normalize");

  // pass3 merge → pass4 mention-resolve → assemble = the name-based BODY (正文).
  // NO asset extraction / id assignment here — the serial pass discovers assets,
  // 造型 and per-scene presence itself from the body. Reuse body.json on a rerun so
  // a serial failure doesn't redo pass3/pass4/assemble.
  let body: NameScript;
  let recordCount: number;
  let noteCount: number;
  if (!opts.force && store.has("body.json")) {
    body = store.readJson<NameScript>("body.json");
    recordCount = store.has("pass3/script.jsonl") ? parseJsonl(store.readText("pass3/script.jsonl")).length : 0;
    noteCount = store.has("notes.json") ? store.readJson<unknown[]>("notes.json").length : 0;
    reused++;
  } else {
    // pass3: merge normalized chunks in source order. Dialogue stays verbatim in
    // its source language; non-dialogue screenplay prose is already localized to
    // Simplified Chinese by pass2. The serial pass resolves identity from context
    // instead of rewriting the body.
    const merged: IngestRecord[] = [];
    for (const s of settled) if (s.ok) merged.push(...s.value);
    store.writeText("pass3/script.jsonl", serializeJsonl(merged));
    const reparsed = parseJsonl(store.readText("pass3/script.jsonl")); // round-trip validation

    // compile: assemble the name-based body (scenes + actions).
    const title = deriveTitle(resolved.path);
    const assembled = assemble(reparsed, { title });
    store.writeJson("notes.json", assembled.notes);
    body = assembled.script;
    store.writeJson("body.json", body);
    recordCount = merged.length;
    noteCount = assembled.notes.length;
  }

  // Serial govern: context starts EMPTY and grows per episode; the model reads each
  // episode's body + context-so-far and reports discovered entities / importance /
  // 造型 / per-scene presence. The governed v3 intermediate is converted to v4. Every step
  // persists the full context md (= prompt) and the model's `# 更新` md; resume
  // replays cached `# 更新` (no LLM). Cleared by --force (serial dir wiped).
  const pad3 = (n: number): string => String(n).padStart(3, "0");
  // One window (1+ contiguous episodes) per `serial/win_<start>_<end>.json`; the
  // filename range is deterministic (pure packing), so resume replays from cache.
  // Content is the model's raw tool-call Delta (JSON) — `.json`, not `.md`. The
  // context_ trace (rendered prompt) and arc_ summary stay markdown.
  const winRel = (startEp: number, endEp: number): string => `serial/win_${pad3(startEp)}_${pad3(endEp)}.json`;
  const serialCheckpoint = {
    readUpdate: (startEp: number, endEp: number): string | undefined => (store.has(winRel(startEp, endEp)) ? store.readText(winRel(startEp, endEp)) : undefined),
    writeStep: (startEp: number, endEp: number, contextMd: string, updateMd: string): void => {
      store.writeText(`serial/context_${pad3(startEp)}.md`, contextMd);
      store.writeText(winRel(startEp, endEp), updateMd);
    },
    readArc: (startEp: number, endEp: number): string | undefined => {
      const rel = `serial/arc_${pad3(startEp)}_${pad3(endEp)}.md`;
      return store.has(rel) ? store.readText(rel) : undefined;
    },
    writeArc: (startEp: number, endEp: number, summaryMd: string): void => {
      store.writeText(`serial/arc_${pad3(startEp)}_${pad3(endEp)}.md`, summaryMd);
    },
    readNameFix: (startEp: number, endEp: number): string | undefined => {
      const rel = `serial/fix_${pad3(startEp)}_${pad3(endEp)}.json`;
      return store.has(rel) ? store.readText(rel) : undefined;
    },
    writeNameFix: (startEp: number, endEp: number, raw: string): void => store.writeText(`serial/fix_${pad3(startEp)}_${pad3(endEp)}.json`, raw),
  };
  timings.phaseStart("serial");
  const serial = await runSerialGovern(body, {
    executor: opts.executor, checkpoint: serialCheckpoint, force: opts.force ?? false,
    ...(sourceRef ? { sourceRef } : {}),
    onWindow: (t) => {
      if (t.index === 0) timings.serialStart(t.total, t.totalEpisodes);
      timings.windowDone(t.startEp, t.endEp, t.ms, t.cached);
      const scope: UsageScope = t.startEp === t.endEp
        ? { kind: "episode", episode: t.startEp }
        : { kind: "episode-range", startEpisode: t.startEp, endEpisode: t.endEp };
      const endedAtMs = Date.now();
      usage.flush({
        scope,
        status: t.cached ? "reused" : "succeeded",
        startedAtMs: endedAtMs - t.ms,
        endedAtMs,
      });
    },
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  const governed = serial.doc;
  reused += serial.reused;
  // Audit: entities the engine auto-created because a merge named an unregistered
  // `into`. Written so this implicit step is inspectable, never silent.
  timings.phaseEnd("serial");
  // Final clean numbering: contiguous per-kind asset ids + per-asset state ids.
  timings.phaseStart("govern");
  const { script: renumbered, idMap } = renumberIds(governed);
  store.writeJson("id_map.json", idMap);
  const intermediateValidation = validateScriptV3(renumbered);
  if (!intermediateValidation.ok) {
    store.writeJson("validation.json", intermediateValidation);
    throw new CliError("Intermediate script failed validation before v4 identity assignment.", {
            exitCode: EXIT_INPUT,
            errorCode: "INGEST_VALIDATION_FAILED",
            hint: "Inspect validation.json.",
            data: { actual: intermediateValidation.errors.slice(0, 5).map((e) => `${e.path}: ${e.message}`) },
          });
  }
  const priorIdentity = !opts.force && store.has("v4_identity_map.json")
    ? store.readJson<V4IdentityMap>("v4_identity_map.json")
    : undefined;
  // V4 identities bind to the stable pre-renumber ingest keys. The contiguous
  // V3 ids in `renumbered` are an audit artifact only: deleting or merging an
  // earlier entity can shift them onto a different surviving entity.
  const converted = newScriptV4FromV3WithIdentity(
    requireScriptV3(governed),
    priorIdentity,
    serial.identityKeys,
  );
  const finalScript = converted.script;
  const structuralValidation = validateScriptV4(finalScript);
  let auditWarnings = [] as ReturnType<typeof assetAuditWarnings>;
  const auditStartedMs = Date.now();
  let auditStatus: "succeeded" | "failed" = "failed";
  try {
    if (structuralValidation.ok) {
      const audit = await auditWorkspaceScriptAssets(store, finalScript, opts.executor, {
        force: opts.force ?? false,
        ...(opts.signal ? { signal: opts.signal } : {}),
      });
      auditWarnings = assetAuditWarnings(audit.report);
    }
    auditStatus = "succeeded";
  } finally {
    usage.flush({
      scope: { kind: "phase", phase: "asset-audit" },
      status: auditStatus,
      startedAtMs: auditStartedMs,
    });
  }
  const validation = {
    ...structuralValidation,
    warnings: [
      ...structuralValidation.warnings,
      ...validateProductionStandard(finalScript),
      ...auditWarnings,
    ],
  };
  store.writeJson("pass5/script.json", finalScript);
  store.writeJson("v4_identity_map.json", converted.identityMap);
  store.writeJson("validation.json", validation);
  if (!validation.ok) {
    throw new CliError("Final script.json failed v4 validation.", {
            exitCode: EXIT_INPUT,
            errorCode: "INGEST_VALIDATION_FAILED",
            hint: "Inspect validation.json.",
            data: { actual: validation.errors.slice(0, 5).map((e) => `${e.path}: ${e.message}`) },
          });
  }
  store.writeJson("script.json", finalScript);
  timings.phaseEnd("govern");
  timings.finish();

  const g = finalScript;
  runStatus = "succeeded";
  return {
    workspacePath: store.root,
    chunks: chunks.length,
    reused,
    records: recordCount,
    notes: noteCount,
    assets: { actors: g.actors?.length ?? 0, locations: g.locations?.length ?? 0, props: g.props?.length ?? 0 },
    scriptPath: store.path("script.json"),
    validation: { ok: validation.ok, errors: validation.errors.length, warnings: validation.warnings.length },
  };
  } finally {
    usage.finish(runStatus);
  }
}

function deriveTitle(sourcePath: string): string {
  return path.basename(sourcePath, path.extname(sourcePath));
}
